import { useEffect, useRef, useState } from "react";
import { useParams, Link, useSearchParams } from "react-router-dom";
import { ShieldCheck, AlertTriangle, Clock, MapPin, Loader2, ArrowLeft, RefreshCw, Share2, Copy, MessageCircle, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { AppTopBar } from "../components/AppScreen";

/* /groups/:id/guardian — mini dashboard that shows every member's
   last-known Guardian check-in so the trip leader can glance once and
   know everyone is OK. Auto-refreshes every 60s. */

const STATE_STYLES = {
  urgent: {
    dot: "bg-rose-600",
    tone: "border-rose-300 bg-rose-50",
    label: "text-rose-800",
    icon: AlertTriangle,
    caption: "Needs attention",
  },
  attention: {
    dot: "bg-amber-500",
    tone: "border-amber-300 bg-amber-50",
    label: "text-amber-800",
    icon: Clock,
    caption: "Heads up",
  },
  sharing: {
    dot: "bg-emerald-500",
    tone: "border-emerald-300 bg-emerald-50",
    label: "text-emerald-800",
    icon: ShieldCheck,
    caption: "Sharing",
  },
  ok: {
    dot: "bg-emerald-500",
    tone: "border-emerald-300 bg-emerald-50",
    label: "text-emerald-800",
    icon: ShieldCheck,
    caption: "OK",
  },
  not_sharing: {
    dot: "bg-zinc-300",
    tone: "border-border bg-card",
    label: "text-muted-foreground",
    icon: MapPin,
    caption: "Not sharing",
  },
};

function relTime(iso) {
  if (!iso) return "—";
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return "—";
  const mins = Math.max(0, Math.round((Date.now() - then) / 60000));
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins} min ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

export default function GroupGuardian() {
  const { id: gid } = useParams();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [searchParams] = useSearchParams();
  const highlight = searchParams.get("highlight"); // "urgent" | null
  const firstUrgentRef = useRef(null);

  const load = async (silent = false) => {
    if (!silent) setBusy(true);
    try {
      const { data } = await api.get(`/groups/${gid}/guardian`);
      setData(data);
    } catch (e) {
      if (!silent) toast.error(e?.response?.data?.detail || "Could not load Guardian dashboard");
    } finally {
      if (!silent) setBusy(false);
    }
  };

  useEffect(() => {
    if (!user) return undefined;
    load();
    const t = setInterval(() => load(true), 60000);
    return () => clearInterval(t);
  }, [user, gid]); // eslint-disable-line react-hooks/exhaustive-deps

  // Scroll the target row into view. Accepts:
  //   ?highlight=urgent      → first member with state==="urgent"
  //   ?highlight=<user_id>   → that specific member (from a push notification)
  useEffect(() => {
    if (!highlight || !data || !firstUrgentRef.current) return;
    const t = setTimeout(() => {
      firstUrgentRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 200);
    return () => clearTimeout(t);
  }, [highlight, data]);

  if (!user) {
    return (
      <div data-testid="group-guardian-signin">
        <AppTopBar title="Group Guardian" />
        <div className="max-w-md mx-auto px-5 py-16 text-center">
          <ShieldCheck className="w-10 h-10 mx-auto text-primary mb-3" />
          <h1 className="font-serif font-800 text-2xl">Sign in to view Group Guardian</h1>
        </div>
      </div>
    );
  }

  const counts = data?.counts;
  return (
    <div data-testid="group-guardian-page">
      <AppTopBar title="Group Guardian" />
      <div className="max-w-3xl mx-auto px-5 py-8">
        <div className="flex items-center gap-2 mb-3">
          <Link to={`/groups/${gid}`} data-testid="group-guardian-back"
            className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-primary">
            <ArrowLeft className="w-4 h-4" /> Back to group
          </Link>
          <button onClick={() => setShareOpen(true)} data-testid="group-guardian-share"
            className="ml-auto inline-flex items-center gap-1 text-xs px-3 py-1 border border-primary text-primary rounded-full font-700 hover:bg-primary hover:text-primary-foreground transition-colors">
            <Share2 className="w-3.5 h-3.5" /> Share view-only
          </button>
          <button onClick={() => load(false)} disabled={busy} data-testid="group-guardian-refresh"
            className="inline-flex items-center gap-1 text-xs text-primary hover:underline disabled:opacity-60">
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />} Refresh
          </button>
        </div>
        {shareOpen && <ShareLinkDialog gid={gid} onClose={() => setShareOpen(false)} />}

        <div className="rounded-2xl border border-border bg-card p-5 mb-4" data-testid="group-guardian-hero">
          <div className="text-xs font-mono uppercase tracking-widest text-accent mb-1">Trip Guardian</div>
          <h1 className="font-serif font-800 text-2xl text-primary">Everyone at a glance</h1>
          <p className="text-sm text-muted-foreground mt-1">Last-known Guardian status for every active member. Refreshes every minute.</p>
          {counts && (
            <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="group-guardian-counts">
              <MiniStat label="Total" value={counts.total} tone="text-primary" />
              <MiniStat label="Sharing" value={counts.sharing} tone="text-emerald-700" />
              <MiniStat label="Heads-up" value={counts.attention} tone="text-amber-700" />
              <MiniStat label="Needs help" value={counts.urgent} tone="text-rose-700" />
            </div>
          )}
        </div>

        {!data ? (
          <div className="py-16 text-center text-muted-foreground"><Loader2 className="w-5 h-5 animate-spin inline" /></div>
        ) : data.members.length === 0 ? (
          <div className="border border-dashed border-border rounded-2xl p-8 text-center bg-card">
            <p className="text-sm text-muted-foreground">No active members in this group yet.</p>
          </div>
        ) : (
          <ul className="space-y-2" data-testid="group-guardian-list">
            {(() => {
              const urgents = data.members.filter((x) => x.state === "urgent");
              const firstUrgentIdx = data.members.indexOf(urgents[0]);
              const targetIdx = highlight && highlight !== "urgent"
                ? data.members.findIndex((x) => x.user_id === highlight)
                : (highlight === "urgent" ? firstUrgentIdx : -1);
              return data.members.map((m, idx) => {
                const style = STATE_STYLES[m.state] || STATE_STYLES.not_sharing;
                const Icon = style.icon;
                const isTarget = idx === targetIdx && targetIdx >= 0;
                return (
                  <li key={m.user_id || m.email}
                    ref={isTarget ? firstUrgentRef : undefined}
                    className={`border rounded-2xl p-4 ${style.tone} ${isTarget ? "ring-2 ring-rose-500 ring-offset-2 ring-offset-background scroll-mt-24" : ""}`}
                    data-testid={`group-guardian-row-${m.user_id || m.email}`}>
                  <div className="flex items-center gap-3">
                    <div className={`w-2.5 h-2.5 rounded-full ${style.dot}`} />
                    <div className="min-w-0 flex-1">
                      <div className="font-700 text-sm truncate flex items-center gap-1.5">
                        {m.name}
                        {m.role === "admin" && <span className="text-[10px] uppercase tracking-widest text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded-full">admin</span>}
                      </div>
                      <div className={`text-xs ${style.label} font-700 inline-flex items-center gap-1`}>
                        <Icon className="w-3 h-3" /> {style.caption}
                        {m.latest_status && (
                          <span className="text-muted-foreground font-500">
                            {" · "}{m.latest_status.status_label} · {relTime(m.latest_status.occurred_at)}
                          </span>
                        )}
                        {m.session && !m.latest_status && (
                          <span className="text-muted-foreground font-500">
                            {" · "}{m.session.mode?.replace("_", " ")} · started {relTime(m.session.starts_at)}
                          </span>
                        )}
                      </div>
                      {m.latest_status?.status_text && (
                        <div className="text-xs text-foreground/80 mt-1 line-clamp-2">"{m.latest_status.status_text}"</div>
                      )}
                      {m.session?.journey_label && (
                        <div className="text-xs text-muted-foreground mt-0.5 truncate">
                          {m.session.journey_label}{m.session.destination ? ` → ${m.session.destination}` : ""}
                        </div>
                      )}
                    </div>
                  </div>
                </li>
                );
              });
            })()}
          </ul>
        )}

        <p className="text-[11px] text-muted-foreground text-center mt-6">
          Statuses are set by each traveller from their own Guardian app. Coordinates are never shared here — only the label they chose to share.
        </p>
      </div>
    </div>
  );
}

const MiniStat = ({ label, value, tone }) => (
  <div className="border border-border bg-background rounded-xl px-3 py-2">
    <div className={`font-serif font-800 text-xl leading-none ${tone}`}>{value ?? 0}</div>
    <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground mt-1">{label}</div>
  </div>
);

/* Admin-only: mint a view-only Guardian share link. Family and other loved
   ones who aren't in the group can peek at "is everyone OK?" during the
   trip. Links auto-expire (default 24h) and admins can revoke early. */
function ShareLinkDialog({ gid, onClose }) {
  const [ttl, setTtl] = useState(24);
  const [busy, setBusy] = useState(false);
  const [links, setLinks] = useState([]);

  const loadLinks = async () => {
    try {
      const { data } = await api.get(`/groups/${gid}/guardian/shares`);
      setLinks(data.items || []);
    } catch { /* silent — 403 for non-admin */ }
  };
  useEffect(() => { loadLinks(); }, []); // eslint-disable-line

  const create = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/groups/${gid}/guardian/share`, { ttl_hours: ttl });
      toast.success("Share link created");
      setLinks((c) => [{
        id: data.share_id, token: data.token, share_url: data.share_url,
        created_at: new Date().toISOString(), expires_at: data.expires_at, revoked_at: null, views: 0,
      }, ...c]);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not create link");
    } finally { setBusy(false); }
  };

  const revoke = async (id) => {
    if (!window.confirm("Revoke this share link? Anyone using it will immediately lose access.")) return;
    try {
      await api.delete(`/groups/${gid}/guardian/share/${id}`);
      setLinks((c) => c.filter((l) => l.id !== id));
      toast.success("Revoked");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not revoke"); }
  };

  const copy = (t) => { navigator.clipboard.writeText(t); toast.success("Link copied"); };
  const wa = (url, expires) => {
    const hrs = Math.max(1, Math.round((new Date(expires).getTime() - Date.now()) / 3600000));
    const text = encodeURIComponent(`Peek at how our group is doing on the trip: ${url} (view-only, expires in ~${hrs}h)`);
    return `https://wa.me/?text=${text}`;
  };

  return (
    <div className="fixed inset-0 z-[80] bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-card border border-border rounded-2xl max-w-lg w-full p-6 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="group-guardian-share-dialog">
        <h3 className="font-serif font-800 text-xl mb-1">Share view-only Guardian link</h3>
        <p className="text-sm text-muted-foreground mb-4">Give family who aren't in the group a peek at "is everyone OK?" during the trip. Coordinates are never shared — only the status each traveller chose.</p>

        <div className="flex items-center gap-2 mb-3" data-testid="group-guardian-share-ttl">
          <label className="text-xs font-mono uppercase tracking-widest text-accent">Expires in</label>
          <select value={ttl} onChange={(e) => setTtl(Number(e.target.value))}
            className="border border-border rounded-lg px-2 py-1.5 text-sm">
            <option value={6}>6 hours</option>
            <option value={24}>24 hours</option>
            <option value={72}>3 days</option>
            <option value={168}>7 days (max)</option>
          </select>
          <button onClick={create} disabled={busy} data-testid="group-guardian-share-create"
            className="ml-auto inline-flex items-center gap-1.5 px-4 py-1.5 bg-primary text-primary-foreground font-700 rounded-full text-sm disabled:opacity-60">
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Share2 className="w-3.5 h-3.5" />} New link
          </button>
        </div>

        <div className="space-y-2" data-testid="group-guardian-share-list">
          {links.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-4">No active share links.</p>
          ) : links.map((l) => (
            <div key={l.id} className="border border-border bg-secondary/40 rounded-lg p-3" data-testid={`group-guardian-share-row-${l.id}`}>
              <div className="text-xs text-muted-foreground mb-1">
                Expires {new Date(l.expires_at).toLocaleString()} · {l.views || 0} view{l.views === 1 ? "" : "s"}
              </div>
              <div className="text-xs font-mono truncate text-primary/80 mb-2">{l.share_url}</div>
              <div className="flex flex-wrap items-center gap-1.5">
                <button onClick={() => copy(l.share_url)} data-testid={`group-guardian-share-copy-${l.id}`}
                  className="inline-flex items-center gap-1 px-3 py-1 border border-border rounded-full text-xs font-700 hover:border-primary">
                  <Copy className="w-3 h-3" /> Copy
                </button>
                <a href={wa(l.share_url, l.expires_at)} target="_blank" rel="noreferrer"
                  data-testid={`group-guardian-share-wa-${l.id}`}
                  className="inline-flex items-center gap-1 px-3 py-1 border border-emerald-300 bg-emerald-50 text-emerald-800 rounded-full text-xs font-700 hover:bg-emerald-100">
                  <MessageCircle className="w-3 h-3" /> WhatsApp
                </a>
                <button onClick={() => revoke(l.id)} data-testid={`group-guardian-share-revoke-${l.id}`}
                  className="ml-auto inline-flex items-center gap-1 px-3 py-1 text-rose-700 hover:bg-rose-50 rounded-full text-xs font-700">
                  <Trash2 className="w-3 h-3" /> Revoke
                </button>
              </div>
            </div>
          ))}
        </div>

        <div className="flex justify-end mt-4">
          <button onClick={onClose} className="px-4 py-2 border border-border rounded-lg text-sm">Close</button>
        </div>
      </div>
    </div>
  );
}
